import { Badge } from 'react-bootstrap';

// A product that is one side of a fork, and the way to the other side.
//
// A transform leaves two products that only make sense together: the source,
// cut down to what it kept, and the fork, holding the metres that left. On
// every list they sit as two unrelated rows — and the question that always
// follows one of them is "where did the rest go". `_forkOf` / `_forkedTo` have
// recorded the answer since the feature was built; nothing showed it outside
// the project's own page.
//
// NEW TAB on purpose: both sides are usually being compared, so following the
// link must not close the list the planner is reading.
//
// The arrows are the ones the transform's own records use — `⇠` for where this
// came from, `⇢` for where part of it went — so the direction reads the same
// wherever it appears.
//
// Reads both spellings of the markers: the jobs list hands out whole job
// documents (`_forkOf`), the bobin-state endpoint reshapes them onto its rows
// (`forkOf`). One component, two callers, no mapping at the call site.
const href = jobId => `/apps/project-management/project-details/${jobId}`;

const Pill = ({ to, title, children }) => (
  <a
    href={href(to)}
    target="_blank"
    rel="noopener noreferrer"
    title={title}
    className="text-decoration-none"
    // The cell is usually itself a link to this product; this one points
    // somewhere else, so it must not be swallowed by the outer navigation.
    onClick={e => e.stopPropagation()}
  >
    <Badge bg="warning" variant="phoenix" className="fs-10 fw-normal">
      {children}
    </Badge>
  </a>
);

const ForkLink = ({ job, className = '' }) => {
  const forkOf = job?._forkOf ?? job?.forkOf ?? null;
  const forkedToRaw = job?._forkedTo ?? job?.forkedTo ?? [];
  const forkedTo = (Array.isArray(forkedToRaw) ? forkedToRaw : [forkedToRaw]).filter(
    f => f && f.jobId
  );

  if (!forkOf?.jobId && !forkedTo.length) return null;

  const label = f => f.productLabel || f.jobCode || String(f.jobId).slice(-6);
  const metres = f =>
    Number(f?.length) > 0 ? ` · ${Number(f.length).toLocaleString('ro-RO')} m` : '';

  return (
    <span className={`d-inline-flex flex-wrap align-items-center gap-1 ${className}`}>
      {forkOf?.jobId && (
        <Pill
          to={forkOf.jobId}
          title={`Forked from ${label(forkOf)} — open that product in a new tab`}
        >
          ⇠ fork of {label(forkOf)}
        </Pill>
      )}

      {/* One pill per fork. Nearly always a single one; several means the
          product was divided more than once, and each still needs its own way
          through. */}
      {forkedTo.map(f => (
        <Pill
          key={f.jobId}
          to={f.jobId}
          title={`${label(f)} was forked off this product${metres(f)} — open it in a new tab`}
        >
          ⇢ forked to {label(f)}
          {metres(f)}
        </Pill>
      ))}
    </span>
  );
};

export default ForkLink;
